import type { PlayLevel } from '@wits/core';
import { parsePlayLevel } from '@wits/core';
import { rememberPlayerId, storedPlayerId } from '../identity/playerId';
import { syncClock } from '../clock/serverClock';
import type {
  ApiErrorBody,
  CalendarResponse,
  ConfigResponse,
  DebugCleared,
  DebugScope,
  FinishResponse,
  Horoscope,
  HoroscopeResponse,
  NightResponse,
  PlayRecord,
  RemoveResponse,
  ShareResponse,
  Sign,
  TodayResponse,
  WordResponse,
} from './types';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody | null,
  ) {
    super(body?.message ?? `Request failed (${status})`);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const id = storedPlayerId();
  if (id) headers.set('X-Player-Id', id);
  if (init.body) headers.set('Content-Type', 'application/json');
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  const text = await response.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!response.ok) throw new ApiError(response.status, data as ApiErrorBody | null);
  const withTime = data as { serverTime?: number } | null;
  if (withTime && typeof withTime.serverTime === 'number') syncClock(withTime.serverTime);
  return data as T;
}

const post = <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) });

export const api = {
  config: () => request<ConfigResponse>('/api/config'),
  async today(lang = 'en'): Promise<TodayResponse> {
    const today = await request<TodayResponse>(`/api/today?lang=${encodeURIComponent(lang)}`);
    rememberPlayerId(today.playerId);
    return today;
  },
  /** A past night; tonight goes through today(). */
  async night(date: string, lang = 'en'): Promise<NightResponse> {
    const night = await request<NightResponse>(`/api/nights/${encodeURIComponent(date)}?lang=${encodeURIComponent(lang)}`);
    rememberPlayerId(night.playerId);
    return night;
  },
  calendar: (month: string, lang = 'en') =>
    request<CalendarResponse>(`/api/calendar?lang=${encodeURIComponent(lang)}&month=${encodeURIComponent(month)}`),
  async level(id: string, version: number): Promise<PlayLevel> {
    return parsePlayLevel(await request<unknown>(`/api/levels/${id}/v${version}`));
  },
  checkWord: (word: string, lang = 'en') => post<{ word: string; valid: boolean }>('/api/words/check', { lang, word }),
  /** Tonight's play on one of its levels, or a past night's when a date is given. */
  startPlay: (levelId: string, date?: string, lang = 'en') => post<PlayRecord>('/api/plays/start', { lang, levelId, ...(date ? { date } : {}) }),
  getPlay: (id: string) => request<PlayRecord>(`/api/plays/${id}`),
  submitWord: (id: string, nodes: number[]) => post<WordResponse>(`/api/plays/${id}/words`, { nodes }),
  removeWord: (id: string, word: string) => post<RemoveResponse>(`/api/plays/${id}/remove`, { word }),
  finishPlay: (id: string) => post<FinishResponse>(`/api/plays/${id}/finish`, {}),
  sharePlay: (id: string) => post<ShareResponse>(`/api/plays/${id}/share`, {}),
  /** Tonight's Horoscope, or a past night's when a date is given. */
  horoscope: (date?: string | null, lang = 'en') =>
    request<HoroscopeResponse>(`/api/horoscope?lang=${encodeURIComponent(lang)}${date ? `&date=${encodeURIComponent(date)}` : ''}`),
  writeHoroscope: (signs: Record<Sign, string>, date?: string | null, lang = 'en') =>
    post<{ horoscope: Horoscope }>('/api/horoscope', { lang, signs, ...(date ? { date } : {}) }),
  /** The debug menu's clear (staging, development): this player's account, a night, or its horoscope; 404 on production. */
  debugClear: (scope: DebugScope, date?: string | null) => post<DebugCleared>('/api/debug/clear', { scope, ...(date ? { date } : {}) }),
};
